import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  useTheme,
  Box,
} from "@mui/material";
import * as React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import Alert from "../../components/alerting/alert";
import useBackend from "../../hooks/use_backend";
import { isDisabled, isReadOnly } from "../../model/device";
import { BatteryLiveData } from "./battery_live_data";
import { isComponentDisabledForUpdates } from "../../pages/login/login_utils";
import { RegisterLiveData } from "./register_live_data";
import { EditDevice } from "./edit_device";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { removeDeviceRequest, saveDeviceRequest } from "../../scripts/api_v2/commands_client";
import { DeviceConnection } from "./device_connection";
import { DeviceMetering } from "./device_metering";
import Snackbar from "../../scripts/snackbar";
import { getTypeAlias } from "../../model/device";
import { isBlockedForCommissioning } from "../../scripts/utils";
import { TOOLTIPS_EN } from "../../enums/tooltip_enum_EN";
import { Tooltip } from "@mui/material";

export function DeviceDialog({ open, device, closeModal, setDevice, removeDevice, permissionMap, required_privilege }) {
  const theme = useTheme();
  const [tab, setTab] = React.useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [alerts, setAlerts] = React.useState([]);
  const params = useParams();
  const backend = useBackend();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  React.useEffect(() => {
    const alerts = siteData ? siteData.alerts.filter((a) => a.device_id == device.device_id) : [];
    setAlerts(alerts);
  }, [siteData, device]);

  async function handleSaveDevice(edits, updatedDevice) {
    if (Object.values(edits).length === 0) {
      return;
    }
    const mutationVariables = { edits: edits, updatedDevice: updatedDevice };
    saveDeviceMutation(mutationVariables);
  }

  const { mutate: saveDeviceMutation } = useMutation({
    mutationFn: async ({ edits, updatedDevice }) => {
      return await saveDeviceRequest(backend.fetch, params.site_id, edits);
    },
    onSuccess: (data, variables) => {
      const { edits, updatedDevice } = variables;
      setDevice(updatedDevice);
      Snackbar.success("Device saved");
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not save device: " + err);
    },
  });

  const { mutate: removeDeviceMutation } = useMutation({
    mutationFn: async () => {
      return await removeDeviceRequest(backend.fetch, params.site_id, device);
    },
    onSuccess: (data) => {
      removeDevice(device);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not remove device: " + err);
    },
  });

  async function handleRemoveDevice(device) {
    if (Object.keys(device).length === 0) return;
    await removeDeviceMutation();
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        closeModal();
      }}
      aria-labelledby="devicedata-dialog-title"
      aria-describedby="devicedata-dialog-description"
      maxWidth={false} // Disable predefined maxWidth
      PaperProps={{
        sx: { maxWidth: 935 }, // Set custom width between 'md' (768px) and 'lg' (1200px)
      }}
    >
      <DeleteModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        removeDevice={() => {
          handleRemoveDevice(device);
          setDeleteModalOpen(false);
          closeModal();
        }}
      />
      <Tooltip title={isBlockedForCommissioning(siteData) ? TOOLTIPS_EN.UPDATES_BLOCKED_DURING_COMMISSIONING : ""}>
        <span>
          {" "}
          {/* https://mui.com/material-ui/react-tooltip/#disabled-elements*/}
          <button
            style={{
              position: "absolute",
              right: 20,
              top: 10,
              background: "transparent",
              border: "none",
              cursor:
                isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey()) ||
                isBlockedForCommissioning(siteData)
                  ? "not-allowed"
                  : "pointer",
              padding: 0,
            }}
            onClick={() => setDeleteModalOpen(true)}
            disabled={
              isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey()) ||
              isBlockedForCommissioning(siteData)
            }
          >
            <DeleteIcon style={{ fontSize: 28 }} />
          </button>
        </span>
      </Tooltip>
      <Stack direction="column" justifyContent="space-between" alignItems="center" display="flex" pb={1}>
        <DialogTitle id="add-dialog-title" sx={{ pb: 0 }}>
          <b style={{ color: theme.palette.secondary.main }}>{getTypeAlias(device.device_type)}</b>
          <b style={{ color: "lightgrey" }}> • </b>
          <b style={{ color: theme.palette.primary.main }}>{device.name}</b>
          {isDisabled(device)
            ? [<b style={{ color: "lightgrey" }}> • </b>, <b style={{ color: theme.palette.error.main }}> Disabled </b>]
            : isReadOnly(device)
              ? [
                  <b style={{ color: "lightgrey" }}> • </b>,
                  <b style={{ color: theme.palette.error.main }}> Read Only </b>,
                ]
              : null}
        </DialogTitle>

        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)} aria-label="basic tabs example">
          <Tab label="Config" value={0} />
          <Tab label="Connection" value={4} />
          <Tab label={"Alerts (" + alerts.length + ")"} value={3} />
          <Tab label="Metering" value={5} />
          <Tab label="Readings" value={1} />
          <Tab label="Registers" value={2} />
        </Tabs>
      </Stack>
      <DialogContent
        sx={{
          minHeight: "500px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflowY: "auto",
          pt: 1,
        }}
      >
        {tab === 0 ? (
          <EditDevice
            device={device}
            setDevice={setDevice}
            handleClose={closeModal}
            handleSaveDevice={handleSaveDevice}
            permissionMap={permissionMap}
            required_privilege={required_privilege}
          />
        ) : null}
        {tab === 1 ? <DeviceReadings device={device} /> : null}
        {tab === 2 ? <DeviceRegisters device={device} /> : null}
        {tab === 5 ? <DeviceMetering device={device} /> : null}
        {/* {tab === 5 ? <DeviceMeteringLive device={device}/> : null} */}
        {tab === 3 ? <AlertTab alerts={alerts} /> : null}
        {tab === 4 ? (
          <DeviceConnection
            device={device}
            setDevice={setDevice}
            handleSaveDevice={handleSaveDevice}
            permissionMap={permissionMap}
            required_privilege={required_privilege}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DeleteModal({ open, setOpen, removeDevice }) {
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Remove device"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to remove this device?
          <br />- This may also remove related data such as its commissioning status
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={removeDevice} autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceReadings({ device }) {
  return <BatteryLiveData deviceId={device.device_id} />;
}

function DeviceRegisters({ device }) {
  return <RegisterLiveData deviceId={device.device_id} />;
}

function AlertTab({ alerts }) {
  return (
    <Box style={{ padding: 10, alignSelf: "flex-start" }}>
      {alerts.map((alert) => (
        <Alert alert={alert} />
      ))}
    </Box>
  );
}
